import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Leaf } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Reveal, KineticLines } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { fetchCollections, fetchPricing, fetchProducts, fetchSiteImages, DISCLAIMER } from "../lib/api";

const HERO_IMG = "/api/images/site-hero";
const STORY_IMG = "/api/images/site-story";

const MANIFESTO = [
  { n: "01", t: "Grown with intention", d: "Every blend begins with a feeling — rest, clarity, comfort — and we build the botanicals around it." },
  { n: "02", t: "Made in small batches", d: "Hand-measured and hand-mixed in Racine, Wisconsin. Nothing mass-produced, nothing rushed." },
  { n: "03", t: "Rooted in ritual", d: "Tea is a pause. A breath. We craft blends that turn ordinary moments into gentle ceremonies." },
];

export default function Home() {
  const [collections, setCollections] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [teas, setTeas] = useState([]);
  const [salves, setSalves] = useState([]);
  const [others, setOthers] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [siteImg, setSiteImg] = useState({});
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    fetchCollections().then(setCollections);
    fetchPricing().then(setPricing);
    fetchProducts({ type: "tea" }).then(setTeas);
    fetchProducts({ type: "salve" }).then(setSalves);
    fetchProducts({ type: "other" }).then(setOthers);
    fetchProducts({ type: "bundle" }).then(setBundles);
    fetchSiteImages().then(setSiteImg).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={siteImg["site-hero"] || HERO_IMG} alt="Pouring herbal tea" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/10 to-transparent" />
        </motion.div>

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-28">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
            className="text-cream text-xs uppercase tracking-[0.3em] mb-6 flex items-center gap-2"
          >
            <Leaf className="w-4 h-4" /> Handcrafted herbal apothecary · Racine, WI
          </motion.p>
          <h1 className="font-serif text-cream text-5xl md:text-7xl lg:text-[6rem] leading-[0.92] tracking-tight max-w-4xl">
            <KineticLines lines={["Steep a moment", <>of <span className="italic text-dusty-rose">stillness</span>.</>]} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <Link
              to="/shop"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-cream text-forest hover:bg-terracotta hover:text-cream pl-7 pr-3 py-3 text-sm uppercase tracking-widest font-semibold"
              style={{ transition: "background-color 0.4s ease, color 0.4s ease" }}
            >
              Shop the Collection
              <span className="w-9 h-9 rounded-full bg-forest text-cream flex items-center justify-center group-hover:bg-cream group-hover:text-forest" style={{ transition: "background-color 0.4s ease, color 0.4s ease" }}>
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <p className="text-cream/80 text-sm max-w-xs leading-relaxed">
              Small-batch loose-leaf teas, salves &amp; botanicals — blended by intention.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-6 border-y border-forest/10 bg-surface">
        <Marquee speed={40} gradient={false} autoFill>
          {["Rest & Restore", "Morning Energy", "Emotional Balance", "Immune Support", "Digestive Comfort", "Women's Wellness"].map((t, i) => (
            <span key={i} className="marquee-item text-forest/70 text-3xl md:text-4xl mx-10">
              {t} <span className="text-terracotta mx-6">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-6">Teas grouped by intention</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] text-forest tracking-tight">
            Find what your body and spirit <span className="italic text-sage">need</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-4 md:col-start-9 flex items-end">
          <p className="text-forest-soft text-lg leading-relaxed">
            Each collection is organized around a feeling, not just a flavor. And when you&rsquo;re
            seeking something all your own, <span className="text-forest font-medium">custom blends are available by request</span>.
          </p>
        </Reveal>
      </section>

      {/* PRICING BLOCK */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <Reveal>
          <div className="rounded-[2.5rem] bg-forest text-cream p-8 md:p-16">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <h2 className="font-serif text-3xl md:text-5xl leading-none">Loose-leaf pricing</h2>
              <p className="text-cream/60 text-sm max-w-xs">Choose your ritual size — from a weekend pot to a season&rsquo;s supply.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {pricing.map((p) => (
                <div
                  key={p.label}
                  data-testid={`pricing-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-3xl p-7 flex flex-col justify-between min-h-[180px] ${p.featured ? "bg-terracotta text-cream" : "bg-cream/5 border border-cream/10"}`}
                >
                  <div>
                    <p className="font-serif text-2xl mb-1">{p.label}</p>
                    <p className={`text-sm ${p.featured ? "text-cream/80" : "text-cream/50"}`}>{p.detail}</p>
                  </div>
                  <p className="font-serif text-5xl mt-6">${p.price}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* COLLECTIONS TABS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16" id="collections">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-4">The Collections</p>
          <h2 className="font-serif text-4xl md:text-6xl text-forest tracking-tight mb-10">Seven ways to feel better.</h2>
        </Reveal>
        {collections.length > 0 && (
          <Tabs defaultValue={collections[0].slug}>
            <TabsList className="flex flex-wrap h-auto bg-transparent gap-2 mb-12 p-0 justify-start">
              {collections.map((c) => (
                <TabsTrigger
                  key={c.slug}
                  value={c.slug}
                  data-testid={`tab-${c.slug}`}
                  className="rounded-full border border-forest/15 px-5 py-2.5 text-sm data-[state=active]:bg-forest data-[state=active]:text-cream data-[state=active]:border-forest text-forest"
                >
                  {c.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {collections.map((c) => (
              <TabsContent key={c.slug} value={c.slug} className="mt-0">
                <p className="font-serif italic text-2xl text-sage mb-2">{c.tagline}</p>
                <p className="text-forest-soft max-w-xl mb-10">{c.description}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
                  {teas.filter((t) => t.collection === c.slug).map((t, i) => (
                    <ProductCard key={t.slug} product={t} index={i} />
                  ))}
                </div>
                {(c.slug === "womens-wellness" || c.slug === "womens-health") && (
                  <div className="mt-10 rounded-2xl bg-dusty-rose/25 border border-dusty-rose/40 p-6" data-testid="womens-disclaimer">
                    <p className="text-sm text-forest leading-relaxed">
                      <strong className="font-semibold">A note on Pregnancy Tea &amp; New Mother Tea:</strong> {DISCLAIMER}
                    </p>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </section>

      {/* SALVES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-4">Apothecary Salves</p>
              <h2 className="font-serif text-4xl md:text-5xl text-forest tracking-tight">Balms for body &amp; rest.</h2>
            </div>
            <p className="hidden md:block font-serif text-2xl text-sage">2oz $12 · 4oz $24</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
          {salves.map((s, i) => <ProductCard key={s.slug} product={s} index={i} />)}
        </div>
      </section>

      {/* OTHER PRODUCTS */}
      <section className="bg-surface py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl text-forest tracking-tight mb-10">More from the shelf.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-x-6 gap-y-12">
            {others.map((o, i) => <ProductCard key={o.slug} product={o} index={i} />)}
          </div>
        </div>
      </section>

      {/* BUNDLES */}
      {bundles.length > 0 && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-4">Gift-worthy Bundles</p>
          <h2 className="font-serif text-4xl md:text-5xl text-forest tracking-tight mb-12">Curated with love.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {bundles.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.1}>
              <Link to={`/product/${b.slug}`} data-testid={`bundle-card-${b.slug}`} className="group block h-full rounded-3xl overflow-hidden bg-forest text-cream">
                <div className="relative h-52 overflow-hidden">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover opacity-80 group-hover:scale-105" style={{ transition: "transform 0.7s ease" }} />
                  <span className="absolute top-4 right-4 font-serif text-3xl bg-terracotta rounded-full w-16 h-16 flex items-center justify-center">${b.price}</span>
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl mb-3">{b.name}</h3>
                  <p className="text-cream/70 text-sm leading-relaxed mb-4">{b.benefits}</p>
                  <ul className="space-y-1.5">
                    {b.contents?.map((c) => (
                      <li key={c} className="text-sm text-dusty-rose flex items-center gap-2"><Leaf className="w-3 h-3" /> {c}</li>
                    ))}
                  </ul>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* MANIFESTO / STORY */}
      <section className="bg-surface py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <Reveal>
              <div className="rounded-[3rem] overflow-hidden">
                <img src={siteImg["site-story"] || STORY_IMG} alt="Holding a cup of tea" className="w-full h-[520px] object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-6">Our Story</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-forest tracking-tight mb-6">
                Born from a kitchen table in <span className="italic text-sage">Racine</span>.
              </h2>
              <p className="text-forest-soft text-lg leading-relaxed mb-4">
                Serendipitous Sips began the way most good things do — slowly, and by accident.
                A search for calm turned into a lifelong study of herbs, and a home kitchen became
                a small-batch apothecary.
              </p>
              <p className="text-forest-soft text-lg leading-relaxed">
                Everything is still handcrafted in Wisconsin, in small batches, with botanicals
                chosen for how they make you feel.
              </p>
            </Reveal>
          </div>

          <div className="space-y-12">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1}>
                <div className="grid md:grid-cols-12 gap-6 items-start border-t border-forest/10 pt-10">
                  <span className="md:col-span-3 font-serif italic text-7xl md:text-8xl text-sage/60 leading-none">{m.n}</span>
                  <h3 className="md:col-span-4 font-serif text-3xl text-forest leading-tight">{m.t}</h3>
                  <p className="md:col-span-5 text-forest-soft text-lg leading-relaxed">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
